/**
 * Sidebar - Responsive collapsible navigation for dashboard
 * Desktop: Fixed sidebar with collapse
 * Mobile sidebar and trigger exported from MobileSidebar.tsx
 */
import { Link, useLocation } from 'react-router-dom'
import {
  Globe,
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  Shield,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  BarChart3,
  Bell,
  Link2,
  Receipt,
} from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { cn } from '../../lib/utils'
import { useAuth } from '@mzanzi/auth'

// Re-export mobile components for backwards compatibility
export { MobileSidebar, MobileSidebarTrigger } from './MobileSidebar'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  badge?: string | number
  disabled?: boolean
  staffOnly?: boolean
}

// Staff/Admin navigation
const staffMainNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Tenants', href: '/tenants', icon: Users, staffOnly: true },
  { label: 'Products', href: '/products', icon: Package },
  { label: 'Orders', href: '/orders', icon: ShoppingCart, badge: 3 },
  { label: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
]

const staffSecondaryNav: NavItem[] = [
  { label: 'Matching', href: '/matching', icon: Link2, staffOnly: true },
  { label: 'Security', href: '/security', icon: Shield, badge: '!', staffOnly: true },
  { label: 'Analytics', href: '/analytics', icon: BarChart3, staffOnly: true },
  { label: 'Notifications', href: '/notifications', icon: Bell, badge: 12 },
]

// Customer navigation (simplified)
const customerMainNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Products', href: '/products', icon: Package },
  { label: 'My Orders', href: '/orders', icon: ShoppingCart },
  { label: 'My Subscriptions', href: '/subscriptions', icon: CreditCard },
]

const customerSecondaryNav: NavItem[] = [
  { label: 'Billing', href: '/billing', icon: Receipt },
  { label: 'Notifications', href: '/notifications', icon: Bell },
]

const bottomNav: NavItem[] = [
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Help', href: '/support', icon: HelpCircle },
]

// Extracted NavLink component
interface NavLinkProps {
  item: NavItem
  isActive: boolean
  collapsed?: boolean
}

function NavLink({ item, isActive, collapsed = false }: NavLinkProps) {
  return (
    <Link
      to={item.disabled ? '#' : item.href}
      className={cn(
        'relative flex items-center gap-3 rounded-lg px-3 text-sm font-medium transition-all',
        'hover:bg-accent hover:text-accent-foreground',
        isActive && 'bg-primary/10 text-primary',
        item.disabled && 'pointer-events-none opacity-50',
        collapsed ? 'justify-center px-2 py-2.5' : 'py-2.5'
      )}
      title={collapsed ? item.label : undefined}
    >
      <item.icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {item.badge && (
            <Badge
              variant={item.badge === '!' ? 'destructive' : 'secondary'}
              className="h-5 min-w-5 px-1.5 text-xs"
            >
              {item.badge}
            </Badge>
          )}
        </>
      )}
      {collapsed && item.badge && (
        <div className="absolute -top-0.5 -right-0.5">
          <div className={cn(
            'h-2 w-2 rounded-full',
            item.badge === '!' ? 'bg-destructive' : 'bg-primary'
          )} />
        </div>
      )}
    </Link>
  )
}

function SidebarHeader({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <>
      <div className={cn(
        'flex h-16 items-center border-b px-4',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-600">
            <Globe className="h-5 w-5 text-white" />
          </div>
          {!collapsed && (
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Mzanzi
            </span>
          )}
        </Link>
        {!collapsed && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>
      {collapsed && (
        <div className="flex justify-center py-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggle}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  )
}

function NavSection({ label, items, collapsed, pathname }: {
  label: string; items: NavItem[]; collapsed: boolean; pathname: string
}) {
  return (
    <div className="space-y-1">
      {!collapsed && (
        <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
      )}
      {items.map((item) => (
        <NavLink key={item.href} item={item} isActive={pathname === item.href} collapsed={collapsed} />
      ))}
    </div>
  )
}

// Desktop Sidebar
interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()
  const { user } = useAuth()

  const isStaff = user?.isStaff ?? false
  const mainNav = isStaff ? staffMainNav : customerMainNav
  const secondaryNav = isStaff ? staffSecondaryNav : customerSecondaryNav

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300',
        'hidden lg:flex lg:flex-col',
        collapsed ? 'w-[70px]' : 'w-[260px]'
      )}
    >
      <SidebarHeader collapsed={collapsed} onToggle={onToggle} />

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <NavSection label="Main" items={mainNav} collapsed={collapsed} pathname={location.pathname} />
        <Separator className="my-4" />
        <NavSection label="Monitor" items={secondaryNav} collapsed={collapsed} pathname={location.pathname} />
      </nav>

      <div className="border-t px-3 py-4 space-y-1">
        {bottomNav.map((item) => (
          <NavLink key={item.href} item={item} isActive={location.pathname === item.href} collapsed={collapsed} />
        ))}
      </div>
    </aside>
  )
}
